import Badge from '@src/components/common/Badge';
import { MODAL_TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import { TOP_LAYER_VARS } from '@src/components/common/topLayerZ';
import styled from 'styled-components';

import type { CoverageVerdict } from '@src/types.ts/translation';
import type { CSSProperties } from 'react';

/** Leading JSON punctuation the coverage endpoint leaks into its prose fields. */
const LEADING_PUNCTUATION = /^[\s:,;{}[\]"'`]+/;

/**
 * A field whose answer is "nothing here" — the token standing alone or followed
 * by punctuation, so a real sentence that merely opens with one of these words
 * ("None of the qualifiers survived") is left alone.
 */
const NEGATIVE_ANSWER = /^(none|n\/a|null|undefined|nothing)\s*([;,.:—-]|$)/i;

/**
 * PLAT-11335 — the readable content of a verdict prose field, or `undefined`
 * when it carries no analysis worth showing.
 *
 * The coverage endpoint's prose fields arrive dirty in three ways, all observed
 * on real Toro content: a raw JSON fragment (":null,"), a leaked ":" separator
 * in front of a NEGATIVE answer (": none; phrase meaning preserved"), and bare
 * filler punctuation ("."). The repository guard passes all of them through
 * untouched (it only checks `typeof === 'string'`), so they used to render
 * under a "Missing" heading as if they were findings.
 *
 * Lives here (not in CoveragePopover) so every coverage surface — popover,
 * badge tooltips — reads fields through the same guard without an import cycle.
 * Deliberately a PRESENTATION fix: the root cause is the arbor-v1 prompt/parse
 * layer (tracked separately) and the verdict is not corrected by this.
 */
export function readVerdictField(
  value: string | undefined,
): string | undefined {
  if (value === undefined) return undefined;
  const cleaned = value.replace(LEADING_PUNCTUATION, '').trim();
  if (!cleaned) return undefined;
  // No letters or digits in ANY script — a bare ".", "…", "-" or similar is
  // filler punctuation, not analysis. (Unicode classes, not [a-z0-9]: the
  // findings quote the translation, which can be in any language.)
  if (!/[\p{L}\p{N}]/u.test(cleaned)) return undefined;
  if (NEGATIVE_ANSWER.test(cleaned)) return undefined;
  return cleaned;
}

/**
 * Which of the three bands a coverage percent falls in (PLAT-10268 / T8).
 * DESIGN.md gives the three-band shape — high / mid / low — but not the cut
 * points; these are this atom's choice: high ≥ 90, mid 70–89, low < 70.
 */
function band(coveragePercent: number): 'high' | 'mid' | 'low' {
  if (coveragePercent >= 90) return 'high';
  if (coveragePercent >= 70) return 'mid';
  return 'low';
}

/**
 * The band's SEMANTIC color — green (high) / gold (mid) / red (low), matching
 * the design system's status triad (`--primary` success, `--accent` "worked with
 * a caveat", `--destructive` failure; see the toast variant table). Used where
 * the verdict is stated at full strength: the coverage popover's headline.
 *
 * PLAT-11335: the mid/low bands used to name `--warning` / `--error`, neither of
 * which exists in `theme.css`, so both always fell through to their hardcoded
 * hex fallback. They now use real tokens.
 */
export function bandColor(coveragePercent: number): string {
  switch (band(coveragePercent)) {
    case 'high':
      return 'var(--primary)';
    case 'mid':
      return 'var(--toast-warning)';
    case 'low':
      return 'var(--destructive)';
  }
}

/**
 * The band's BADGE TEXT color, from the design system's dedicated badge/tag
 * scale (`--badge-*-text`).
 *
 * Badges have their own, stricter color contract than the semantic triad above
 * (documented in `src/stories/ui-kit/Badges.stories.tsx`): the background is
 * always neutral and "never tinted with the label color", the border is always
 * `--border-subtle`, and the semantic color applies to the TEXT only — using the
 * desaturated `--badge-*-text` token, not the saturated base. Passing
 * `--primary` here would be the saturated base on a transparent pill, which is
 * how this badge came to render as a loud colored ring competing with the
 * transcript text beside it (PLAT-11335).
 */
export function bandBadgeTextColor(coveragePercent: number): string {
  switch (band(coveragePercent)) {
    case 'high':
      return 'var(--badge-green-text)';
    case 'mid':
      return 'var(--badge-orange-text)';
    case 'low':
      return 'var(--badge-red-text)';
  }
}

interface CoverageBadgeProps {
  verdict: CoverageVerdict;
  /**
   * Opens the coverage details popover (PLAT-10268 Atom 6). Receives the badge's
   * own DOM element so the caller can anchor the popover to this fragment. When
   * unset the badge stays display-only (its Atom-5 behavior), so the click is
   * purely additive.
   */
  onClick?: (anchorElement: HTMLElement) => void;
  /**
   * HOVER is the primary way in (Denys, 2026-07-14): pointing at the score shows
   * why it is what it is and what would improve it. Fires on mouse-enter and on
   * keyboard focus — focus included so the details are not mouse-only. The caller
   * owns the open delay and the hover bridge into the popover; this component
   * only reports intent.
   */
  onHoverOpen?: (anchorElement: HTMLElement) => void;
  /** Hover/focus left the badge — the caller decides when to actually close. */
  onHoverClose?: () => void;
}

/**
 * A compact pill showing a translated span's coverage percent, banded by
 * severity. Reuses the design-system `Badge` and — per the badge color contract
 * — colors only its TEXT, leaving the neutral background and subtle border the
 * component already defaults to. Clicking it opens the coverage details popover
 * (analysis / old-vs-current / Undo) when `onClick` is provided; without
 * `onClick` it is display-only. Rendered in an analyzed block's dedicated
 * coverage row in the Translation Review translated column; the caller decides
 * (via `entryForPhrase`) whether a fresh verdict exists before mounting it, so
 * this component always has a verdict to show.
 */
export function CoverageBadge({
  verdict,
  onClick,
  onHoverOpen,
  onHoverClose,
}: CoverageBadgeProps) {
  const interactive = onClick != null || onHoverOpen != null;
  return (
    <BadgeWrap
      $clickable={interactive}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={
        interactive
          ? `Coverage ${verdict.coveragePercent}% — open details`
          : undefined
      }
      onMouseEnter={onHoverOpen ? e => onHoverOpen(e.currentTarget) : undefined}
      onMouseLeave={onHoverClose}
      // Keyboard parity: focus opens the same details hover opens, so the
      // explanation is reachable without a mouse.
      onFocus={onHoverOpen ? e => onHoverOpen(e.currentTarget) : undefined}
      onBlur={onHoverClose}
      onClick={onClick ? e => onClick(e.currentTarget) : undefined}
      onKeyDown={
        onClick
          ? e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick(e.currentTarget);
              }
            }
          : undefined
      }
    >
      {/* Text color only. `backgroundColor` and `borderColor` are deliberately
          NOT passed so they fall through to Badge's own neutral --gray-700 /
          --border-subtle defaults, which is the documented badge treatment. */}
      <Badge size="sm" textColor={bandBadgeTextColor(verdict.coveragePercent)}>
        {verdict.coveragePercent}%
      </Badge>
    </BadgeWrap>
  );
}

// Centers the pill within its dedicated coverage row. Clickable when the details
// popover is wired (Atom 6).
const BadgeWrap = styled.span<{ $clickable?: boolean }>`
  vertical-align: middle;
  cursor: ${p => (p.$clickable ? 'pointer' : 'default')};
`;

/**
 * Style for the ONE portaled `<Tooltip>` a coverage-badge surface mounts.
 *
 * Not bare `MODAL_TOOLTIP_STYLE`: its resting z fallback is 1000003, and the
 * whole-story review workspace overlay sits at `REVIEW_WORKSPACE_Z` = 1000004 —
 * one ABOVE it — so the tooltip opened invisibly BEHIND the workspace (the
 * `--top-layer-tooltip` var only lifts tooltips while the clip-preview review
 * stack is mounted). Resting fallback 1000006 clears the workspace and the
 * context menus' historical 1000005 (`SpeakerContextMenu` precedent); the var
 * still wins (1000050) inside the clip-preview stack. Guarded by a unit test
 * against `REVIEW_WORKSPACE_Z`.
 */
export const COVERAGE_TOOLTIP_STYLE: CSSProperties = {
  ...MODAL_TOOLTIP_STYLE,
  maxWidth: '280px',
  zIndex: `var(${TOP_LAYER_VARS.tooltip}, 1000006)`,
};

interface CoverageBadgeWithTooltipProps {
  verdict: CoverageVerdict;
  /**
   * The react-tooltip id of the host surface's ONE portaled `<Tooltip>` —
   * many badges share it via data attributes (the `UndoRedoButtons` idiom).
   */
  tooltipId: string;
}

/**
 * PLAT-11335 — the display-only badge for surfaces where the full coverage
 * popover would be too much (the phrase-editor popover, the Changes tab): the
 * pill with the verdict's one-line explanation as a hover tooltip. When the
 * explanation carries no readable analysis (`readVerdictField`) the tooltip is
 * simply absent and the badge stands alone.
 */
export function CoverageBadgeWithTooltip({
  verdict,
  tooltipId,
}: CoverageBadgeWithTooltipProps) {
  const explanation = readVerdictField(verdict.explanation);
  return (
    <span
      data-testid="coverage-badge-tooltip-anchor"
      data-tooltip-id={explanation ? tooltipId : undefined}
      data-tooltip-content={explanation}
    >
      <CoverageBadge verdict={verdict} />
    </span>
  );
}
